import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { MagnifyingGlass, Buildings, X, ArrowRight } from "@phosphor-icons/react";

/**
 * AdminPhotoSearch — cross-property photo library search.
 *
 * Query hits `/api/photos/search?q=...&tag=...&kind=...` which walks
 * the tenant's WOs and matches caption / ai_tags / ai_note. Common tags
 * are surfaced as one-tap filter chips so a landlord "have we ever
 * had a leak here" question turns into a two-tap answer.
 */
const COMMON_TAGS = [
  "Damage Detail", "Plumbing", "Electrical", "Safety Concern",
  "Structural", "Cleanliness Issue", "Wear and Tear", "Access Point",
];

export default function AdminPhotoSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [tag, setTag] = useState(params.get("tag") || "");
  const [kind, setKind] = useState(params.get("kind") || "");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const search = async () => {
    setBusy(true);
    try {
      const r = await api.get("/photos/search", { params: { q, tag, kind, limit: 80 } });
      setResults(r.data?.results || []);
    } catch { setResults([]); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    // Only fire when there's something to search — API refuses to dump everything.
    if (q || tag || kind) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tag) p.set("tag", tag);
    if (kind) p.set("kind", kind);
    setParams(p);
    search();
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-photo-search">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="font-display font-black text-3xl">Photo Library</h1>
          <span className="text-xs font-black uppercase tracking-widest text-slate-500">
            Search every photo across every property
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                placeholder="Search by caption, AI note, or word (e.g. 'leak', 'kitchen', 'drywall')"
                data-testid="photo-search-input"
                className="w-full h-11 pl-10 pr-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
            </div>
            <button onClick={doSearch} disabled={busy}
              data-testid="photo-search-go"
              className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs rounded-md">
              {busy ? "…" : "Search"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 mr-1">Common tags:</span>
            {COMMON_TAGS.map(t => (
              <button key={t} onClick={() => { setTag(t === tag ? "" : t); setTimeout(doSearch, 30); }}
                data-testid={`photo-search-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`h-7 px-2 border-2 font-black uppercase text-[10px] tracking-widest transition ${
                  tag === t ? "bg-cyan-600 border-cyan-700 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-cyan-500"
                }`}>
                {t}
              </button>
            ))}
            <span className="mx-2 h-5 w-px bg-slate-300"/>
            {["before", "after"].map(k => (
              <button key={k} onClick={() => { setKind(k === kind ? "" : k); setTimeout(doSearch, 30); }}
                data-testid={`photo-search-kind-${k}`}
                className={`h-7 px-2 border-2 font-black uppercase text-[10px] tracking-widest transition capitalize ${
                  kind === k ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
                }`}>
                {k}
              </button>
            ))}
            {(q || tag || kind) && (
              <button onClick={() => { setQ(""); setTag(""); setKind(""); setResults([]); setParams(new URLSearchParams()); }}
                data-testid="photo-search-clear"
                className="h-7 px-2 text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-700 inline-flex items-center gap-1">
                <X size={12} weight="bold"/> Clear
              </button>
            )}
          </div>
        </div>

        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3" data-testid="photo-search-count">
          {busy ? "Searching…" : results.length > 0 ? `${results.length} photo${results.length === 1 ? "" : "s"}` : (q || tag || kind) ? "No matches" : "Type a query or pick a tag to begin"}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-search-results">
          {results.map((r, i) => (
            <div key={i} className="group rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition"
                 data-testid={`photo-search-result-${i}`}>
              <button onClick={() => setLightbox(r)} className="block w-full aspect-square bg-slate-100 relative overflow-hidden">
                <img src={`${API.replace(/\/api$/, "")}${r.url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                <span className="absolute top-1.5 right-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/90 text-[#0F1F3A] capitalize">
                  {r.kind}
                </span>
                {(r.caption || r.ai_note) && (
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-[#0F1F3A]/95 to-transparent text-[10px] font-black text-white line-clamp-2 leading-tight">
                    {r.caption || r.ai_note}
                  </div>
                )}
              </button>
              <div className="p-2">
                <Link to={`/admin/jobs/${r.job_id}`} className="text-[10px] font-black text-slate-700 hover:text-cyan-700 truncate inline-flex items-center gap-1">
                  {r.work_order_number ? `WO ${r.work_order_number}` : r.job_title.slice(0, 28) || "Open WO"} <ArrowRight size={10}/>
                </Link>
                <div className="text-[9px] font-bold text-slate-500 truncate inline-flex items-center gap-1 mt-0.5">
                  <Buildings size={9} weight="fill"/> {r.property_address || "—"}
                </div>
                {(r.ai_tags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.ai_tags.slice(0, 2).map((t, ti) => (
                      <span key={ti} className="text-[8px] font-black uppercase tracking-widest px-1 py-0.5 bg-cyan-100 text-cyan-800 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {lightbox && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
            <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
              <img src={`${API.replace(/\/api$/, "")}${lightbox.url}`} alt="" className="max-h-[75vh] mx-auto rounded-2xl"/>
              <div className="mt-3 text-center text-white/90">
                {lightbox.caption && <div className="font-black text-lg">{lightbox.caption}</div>}
                <div className="text-sm text-white/60 capitalize mt-1">
                  {lightbox.kind} · {lightbox.property_address}
                  {lightbox.work_order_number ? ` · WO ${lightbox.work_order_number}` : ""}
                </div>
                <Link to={`/admin/jobs/${lightbox.job_id}`} className="mt-3 inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-xs">
                  Open Work Order <ArrowRight size={12}/>
                </Link>
              </div>
            </div>
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl">×</button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
